import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@ff-it/ui';
import type { ReactElement } from 'react';

import type { BandSet, DayType, HourRange } from '../types';
import type { BandRateColumn } from './BandRateTable';
import { BAND_COLUMN_WIDTH, dayTypeLabels } from './const';
import { getBandGroups, getBandKey, getBandlabel } from './util';

const MIN_CELL_WIDTH = 52;

/** One table per band group, laid out side by side. Columns are resolved per
 * group, so a group renders only the columns that apply to it — unlike
 * BandRateTable, which shares one header across every group. Geometry without a
 * day-type split renders a single table. */
export function BandGroupTables<T extends HourRange>({
  bands,
  columns,
}: {
  bands: BandSet<T>;
  columns: (dayType: DayType) => BandRateColumn<T>[];
}): ReactElement {
  return (
    <Box display="flex" gap="3" alignItems="flex-start">
      {getBandGroups(bands).map(({ dayType, bands: groupBands }) => {
        const groupColumns = columns(dayType);
        return (
          <Box
            key={dayType}
            style={{
              flex: `${groupColumns.length} 1 ${BAND_COLUMN_WIDTH}px`,
              minWidth: `${BAND_COLUMN_WIDTH + groupColumns.length * MIN_CELL_WIDTH}px`,
            }}
          >
            <Table size="xs" textSize="sm" border="subtle" fixed>
              <colgroup>
                <col style={{ width: `${BAND_COLUMN_WIDTH}px` }} />
              </colgroup>
              <Thead variant="default">
                <Tr>
                  <Th style={{ whiteSpace: 'nowrap' }}>{dayTypeLabels[dayType]}</Th>
                  {groupColumns.map((column) => (
                    <Th key={column.key} textAlign="right">
                      {column.header}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {groupBands.map((band) => {
                  const bandKey = getBandKey(dayType, band);
                  return (
                    <Tr key={bandKey}>
                      <Td>{getBandlabel(band)}</Td>
                      {groupColumns.map((column) => (
                        <Td
                          key={column.key}
                          textAlign="right"
                          className={column.cellClassName?.(band, dayType, bandKey)}
                        >
                          {column.render(band, dayType, bandKey)}
                        </Td>
                      ))}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        );
      })}
    </Box>
  );
}
